<template>
  <div>
    <div v-if="error && enableEdit" class="message-errors">
      <label class="label">{{ error }}</label>
    </div>
    <div class="d-flex justify-content-between align-items-center gf_spacing_pack-element">
      <Teleport v-if="isOpenTooltip" to="#tooltips">
        <Tooltip :pointer="true" t-class="text-center" :width="'auto'" :delay="100" />
      </Teleport>
      <span class="gf_spacing_pack-element--name" :class="elementNameHoverClass">
        <input
          v-show="enableEdit"
          :ref="`editInput${index}`"
          type="text"
          :value="element.name"
          class="gf_spacing_pack-element--name--edit"
          :class="elementInputNameClass"
          placeholder="New style name"
          @focus="setValueBeforeInput"
          @focusout="focusOutInput($event, index)"
          @keyup.enter="saveElementName($event, index)"
          @keyup.esc="saveElementName($event, index)"
          @change.prevent="saveElementName($event, index)"
          @input.prevent="onInput($event)" />
        <span
          v-show="!enableEdit"
          :disabled="!isReadyToSave"
          class="gf_spacing_pack-element--name--edit gf_spacing_pack-element--name--display"
          @click="elementNameClick(index, true)"
          @mouseover="elementNameHover(true)"
          @mouseleave="elementNameHover(false)">
          {{ element.name }}
        </span>
        <span class="gf_spacing_pack-element--actions">
          <i
            v-if="enableDuplicate"
            v-popover:tooltip.top="'Duplicate'"
            class="icon icon-clone gf_spacing_pack-element--actions--copy"
            @mouseover="isOpenTooltip = true"
            @mouseleave="isOpenTooltip = false"
            @click="duplicateItem(index)" />
          <i
            v-if="element.isDefault"
            v-popover:tooltip.top="'You can not delete default ' + element.type"
            class="icon icon-delete gf_spacing_pack-element--actions--remove"
            disabled
            @mouseover="isOpenTooltip = true"
            @mouseleave="isOpenTooltip = false" />
          <i
            v-else
            v-popover:tooltip.top="'Coming soon'"
            class="icon icon-delete gf_spacing_pack-element--actions--remove"
            disabled
            @mouseover="isOpenTooltip = true"
            @mouseleave="isOpenTooltip = false" />
        </span>
      </span>
      <div class="gf_spacing_input">
        <GfInputUnit
          :id="index"
          :units="inputUnits"
          :disabled="!isReadyToSave"
          :required="true"
          :value="convertValue(element.value.value, 'number')"
          @invalid="inputInvalid"
          @change="updateValuePreset"
          @onChange="updateValuePreset" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import type { PresetOption } from '@/types/global-style';

interface DataInterface {
  inputUnits: any;
  elementNameHoverClass: any;
  elementInputNameClass: any;
  enableEdit: any;
  error: any;
  isOpenTooltip: boolean;
  oldNameValue: any;
}

interface SpacingInterface {
  convertValue(value: any, type: string): any;
  updateValuePreset(data: { value: any; unit: string; id: number }): void;
  isInvalidName(name: string): boolean;
  saveElementName(e: { target: { value: string } }, index: number): void;
  deleteElement(index: number): void;
  elementNameClick(index: number, isShow: boolean): void;
  elementNameHover(state: boolean): void;
  generateName(index: number): string;
  duplicateItem(index: number): void;
  onInput(e: any): void;
}

export default defineComponent({
  name: 'SpacingItem',
  props: {
    element: {
      type: Object,
    },
    index: {
      type: Number,
    },
    enableDuplicate: {
      type: Boolean,
    },
    isReadyToSave: {
      type: Boolean,
    },
  },
  data: (): DataInterface => ({
    inputUnits: [
      {
        name: 'px',
        value: 'px',
      },
    ],
    elementNameHoverClass: '',
    elementInputNameClass: '',
    enableEdit: false,
    error: null,
    isOpenTooltip: false,
    oldNameValue: '',
  }),
  computed: {
    ...mapGetters('sidebar', {
      currentPresetOptions: 'getCurrentPresetOptions',
    }),
  },
  watch: {
    currentPresetOptions() {
      if (!this.isInvalidName(this.element.name)) {
        this.elementNameClick(this.index, false);
      }
    },
    enableDuplicate(val) {
      if (!val) {
        this.isOpenTooltip = false;
      }
    },
  },
  created() {
    if (
      this.element.id &&
      this.element.id.toString().includes('gpid_') &&
      parseInt(this.element.id.replace('gpid_', '')) >= new Date().getTime() - 1000
    ) {
      this.elementNameClick(this.index, true);
    }
  },
  methods: {
    setValueBeforeInput() {
      this.oldNameValue = this.element.name;
    },
    onInput(e: any) {
      const value = e.target.value.trim();

      this.element.name = value;
      this.isInvalidName(value);
    },
    isInvalidName(name: string): boolean {
      const that = this as any;
      that.elementInputNameClass = 'error';
      that.$emit('isValid', false);

      if (!name) {
        that.error = this.$t('sidebarGS.styleDetail.general.errorInputNameBlank');
        return true;
      }

      if (name.length > 4) {
        that.error = this.$t('sidebarGS.styleDetail.general.errorInputNameMax', { maxLength: 4 });
        return true;
      }

      const existItem = that.currentPresetOptions
        .filter((element: { destroy: any }) => !element.destroy)
        .filter((preset: PresetOption) => preset.name.toLowerCase() === name.toLowerCase());

      if (existItem.length > 1) {
        that.error = this.$t('sidebarGS.styleDetail.general.errorInputNameExisted');
        return true;
      }

      that.$emit('isValid', true);
      that.error = null;
      that.elementInputNameClass = '';
      return false;
    },
    focusOutInput(e: any, index: number): void {
      if (this.error) this.element.name = this.oldNameValue;
      this.saveElementName(e, index);
    },
    saveElementName(e: any, index: number): void {
      e.target.blur();
      if (!this.isInvalidName(this.element.name)) {
        this.elementNameClick(index, false);
      }

      return;
    },
    deleteElement(index: number): void {
      // this.element.destroy = true;
      this.$emit('onDelete', index);
    },
    elementNameClick(index: number, isShow: boolean): void {
      this.enableEdit = isShow;
      this.elementInputNameClass = '';
      if (isShow) {
        this.elementNameHoverClass = 'editing';
        setTimeout(() => {
          (this.$refs as any)[`editInput${index}`].focus();
        }, 100);
      } else {
        this.elementNameHoverClass = '';
        this.enableEdit = false;
      }
    },
    elementNameHover(state: boolean): void {
      if (this.elementNameHoverClass == 'editing') return;

      this.elementNameHoverClass = state ? 'hover' : '';
    },
    generateName(index: number): string {
      return `Spacing ${index}`;
    },
    inputInvalid(invalid: boolean): void {
      this.$emit('isValid', !invalid);
    },
    updateValuePreset(data: { value: any; unit: string; id: number }): void {
      if (!data.value) {
        data.value = 0;
      }
      const value: any = this.convertValue(data.value, data.unit);
      this.element.value.value = value;
      return;
    },
    duplicateItem(index: number): void {
      this.$emit('onDuplicate', index);
    },
    convertValue(value: any, type: string): any {
      if (type == 'number') {
        return value.toString().replace(/[a-zA-Z]/g, '');
      } else {
        return `${value}${type}`;
      }
    },
  } as any,
});
</script>

<style scoped lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.message-errors {
  background: map-get($text-light-mode, 'low-contrast');
  border-radius: 4px;
  max-width: 172px;
  @include flex-center;
  padding: 4px 0;
  margin-left: -3px;
  position: relative;
  min-height: 44px;
  margin-top: 6px;

  .label {
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 14px;
    color: map-get($light-grey, '600');
    margin-left: 8px;
    margin-right: 8px;
    padding-bottom: 0;
  }

  &:after {
    content: '';
    position: absolute;
    left: 30px;
    bottom: -6px;
    max-width: 173px;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid map-get($text-light-mode, 'low-contrast');
    clear: both;
  }
}

.gf_spacing {
  color: $neutral_6;
  padding: 8px;

  &_pack-name {
    font-size: 11px;
    font-weight: 500;
    margin-bottom: 17px;
    margin-top: 9px;

    &--value {
      margin-right: 5.5px;
    }

    &--notice {
      width: 13px;
    }
  }

  &_pack-elements {
    background: $neutral_2;
    border-radius: 4px;
    padding: 8px;
  }

  &_pack-element {
    border-bottom: 1px solid $neutral_3;
    font-size: 12px;
    padding: 8px 0;

    &:hover {
      .gf_spacing_pack-element--actions {
        display: flex;
        transition: all 200ms;
      }

      .gf_spacing_pack-element--name {
        padding-right: 50px;

        &.editing {
          padding-right: 0;

          .gf_spacing_pack-element--actions {
            display: none;
          }
        }

        &.hover {
          border: 1px solid map-get($hybrid-shade-light, '500');
          transition: border 200ms;
          padding-right: 0;

          .gf_spacing_pack-element--actions {
            display: none;
          }
        }
      }
    }

    &--name {
      border-radius: 3px;
      border: 1px solid transparent;
      color: map-get($text-dark-mode, 'high-contrast');
      margin-left: -4px;
      padding: 1px 0 1px 4px;
      position: relative;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: calc(100% - 84px);

      &.editing {
        .gf_spacing_pack-element--name--edit {
          &.error {
            border-color: $accent-red-new;
          }
        }
      }

      &--edit {
        background: transparent;
        border-radius: 3px;
        border: 1px solid map-get($primary-blue, '275');
        box-sizing: border-box;
        color: map-get($text-dark-mode, 'high-contrast');
        font-size: 12px;
        margin-left: -4px;
        padding: 4px;
        width: calc(100% + 4px);
      }

      &--display {
        width: 70px;
        color: #ffffff;
        background-color: unset;
        border: unset;
      }
    }

    &--actions {
      display: none;
      position: absolute;
      right: 0;
      top: 1px;
      align-items: center;

      &--remove {
        border-radius: 3px;
        cursor: pointer;
        padding: 4px;
        height: 24px;
        width: 24px;
        font-size: 16px;
        display: flex;
        justify-content: center;
        align-items: center;
        color: $text-dark-disabled;
        path {
          fill: currentColor;
        }

        // &:hover {
        //   background: map-get($accent-color-red, "400");

        //   path {
        //     fill: map-get($text-dark-mode, "high-contrast");
        //   }
        // }
      }

      &--copy {
        background: map-get($hybrid-shade-dark, '300');
        border-radius: 3px;
        cursor: pointer;
        margin-right: 4px;
        padding: 4px;
        height: 24px;
        width: 24px;
        font-size: 16px;
        display: flex;
        justify-content: center;
        align-items: center;

        &:hover {
          background: map-get($hybrid-shade-dark, '200');
        }
      }
    }
  }

  &_pack-actions {
    border-radius: 3px;
    border: 1px dashed $neutral_5;
    cursor: pointer;
    margin-top: 16px;
    padding: 9px 0px;

    &--label {
      font-size: 12px;
      line-height: 14px;
      color: $textDarkHigh;
    }

    &--icon {
      width: 16px;
      margin-right: 10px;
    }
  }

  &_input {
    width: 80px;
  }
}
</style>
